import React, { useState, useEffect, useRef } from 'react';

export default function MultiSelect({
    options = [],
    selected,
    onChange,
    label,
    hint,
    placeholder = 'Select...',
    loading = false,
    allowAll = false,
    allLabel = 'All',
    className = '',
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isAllSelected = allowAll && selected === 'all';
    const selectedArray = isAllSelected ? [] : (Array.isArray(selected) ? selected : []);
    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(search.toLowerCase()) ||
        opt.value.toLowerCase().includes(search.toLowerCase())
    );

    const toggleOption = (value) => {
        if (isAllSelected) {
            onChange([value]);
        } else if (selectedArray.includes(value)) {
            onChange(selectedArray.filter(v => v !== value));
        } else {
            onChange([...selectedArray, value]);
        }
    };

    const removeOption = (value) => {
        onChange(selectedArray.filter(v => v !== value));
    };

    const handleAllToggle = () => {
        onChange(isAllSelected ? [] : 'all');
    };

    return (
        <div className={className}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {label}
                    {hint && <span className="text-xs text-gray-400 font-normal ml-1.5">{hint}</span>}
                </label>
            )}
            <div className="relative" ref={dropdownRef}>
                <div
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full min-h-[36px] rounded-md border border-gray-200 bg-white px-2.5 py-1.5 cursor-pointer flex flex-wrap gap-1 items-center hover:border-gray-300 transition-colors"
                >
                    {isAllSelected ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-900 text-white">
                            {allLabel}
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onChange([]); }}
                                className="text-gray-300 hover:text-white"
                            >
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </span>
                    ) : selectedArray.length > 0 ? (
                        selectedArray.map(value => {
                            const opt = options.find(o => o.value === value);
                            return (
                                <span key={value} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                    {opt?.label || value}
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); removeOption(value); }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </span>
                            );
                        })
                    ) : (
                        <span className="text-sm text-gray-400">{placeholder}</span>
                    )}
                </div>

                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                        <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search..."
                                className="w-full h-8 border border-gray-200 rounded-md px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>

                        {loading ? (
                            <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
                        ) : (
                            <div className="py-1">
                                {allowAll && (
                                    <div
                                        onClick={handleAllToggle}
                                        className={`px-3 py-2 cursor-pointer flex items-center justify-between border-b border-gray-100 ${isAllSelected ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                                    >
                                        <span className="text-sm font-medium text-gray-900">{allLabel}</span>
                                        {isAllSelected && (
                                            <svg className="w-4 h-4 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                            </svg>
                                        )}
                                    </div>
                                )}
                                {filteredOptions.length === 0 ? (
                                    <div className="px-3 py-3 text-sm text-gray-500 text-center">No options found</div>
                                ) : (
                                    filteredOptions.map(opt => {
                                        const isSelected = selectedArray.includes(opt.value) && !isAllSelected;
                                        return (
                                            <div
                                                key={opt.value}
                                                onClick={() => toggleOption(opt.value)}
                                                className={`px-3 py-2 cursor-pointer flex items-center justify-between ${isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                                            >
                                                <div className="min-w-0">
                                                    <div className="text-sm text-gray-900 truncate">{opt.label}</div>
                                                    {opt.description && <div className="text-xs text-gray-500 truncate">{opt.description}</div>}
                                                </div>
                                                {isSelected && (
                                                    <svg className="w-4 h-4 text-gray-900 shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                                    </svg>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
